import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Base64 } from "js-base64";
import { useSelector } from "react-redux";
import { styled } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from '@mui/material/Chip';
import Dialog from "@mui/material/Dialog";
import Drawer from '@mui/material/Drawer';
import Button from "@mui/material/Button";
import Switch from '@mui/material/Switch';
import SvgIcon from "@mui/material/SvgIcon";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import FormGroup from "@mui/material/FormGroup";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DialogTitle from "@mui/material/DialogTitle";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { origin } from "../../Config/Api-constants";
import GlobalListOptions from "./GlobalListOptions";
import "./FroalaEditorStyles.scss";
import "../../../Assets/Styles/Containers/_commentLibraryModal.scss";
import "../../../Assets/Styles/Containers/_globalList.scss";
import { arrowDown } from "../../../Assets/Svg/Svg";
import { getTextColor } from "../../Helpers/HelperFunctions";

function EditOptionsDialog(props) {
  const { state, setState, handleSave, id, dialogRef, handleDeleteDropdown, filterTagList, addDropdown, defaultCategories, selectedFilterTagID: categorySelectedFilterTagID, isTexasTemplate, categoryId } = props;
  const { open, newOption, dialogOptionList, globalLists, saveToGlobalLists, name, tags, description, listId, listGuid, globalListsFetchNonce } = state;
  const storeData = useSelector((state) => state.CommonReducer);
  const rwSettingAccess = storeData?.inspectionData?.data?.REPORT_WRITER_SETTINGS_ACCESS;
  const finalFilterTagList = Array.isArray(filterTagList) ? filterTagList : Array.isArray(storeData?.clTag) ? storeData?.clTag : [];
  const [selectedFilterTagID, setSelectedFilterTagID] = useState(null);
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(-1);
  const [tagOpen, setTagOpen] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [tagInputValue, setTagInputValue] = useState("");

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const editInputRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    if (open && isEdit !== -1 && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [isEdit, dialogOptionList?.length, open]);

  const handleTagOpen = (e) => {
    e.stopPropagation();
    setTagOpen(true);
  };

  const handleTagClose = (e) => {
    e.stopPropagation();
    setTagOpen(false);
  };

  const handleCloseDialog = () => {
    setState((prevState) => ({ ...prevState, open: false, dialogOptionList: [], textfield: false, editedOption: null, globalLists: [], name: "", tags: [], description: "", saveToGlobalLists: false, dialogOptionList: [], selectedFilterTagID: null, listId: null, listGuid: null }));
    setSearchData(null);
    setSelectedFilterTagID(null)
    setPage(0);
    pageRef.current = 0;
    hasMoreRef.current = true;
    isInitialMountRef.current = true;
  };

  const handleOptionBlur = () => {
    if (isEdit !== -1) {
      setIsEdit(-1);
      setState((prevState) => ({ ...prevState, newOption: "" }));
    }
  };

  const handleOptionClick = (option, index) => {
    if (isEdit !== index) {
      if (isEdit !== -1) {
        setIsEdit(-1);
        setState((prevState) => ({ ...prevState, newOption: "" }));
      }
      setIsEdit(index);
      setState((prevState) => ({ ...prevState, newOption: option }));
    }
  };

  const handleOptionChange = (e, index) => {
    if (isEdit === index) {
      const value = e.target.value;
      setState((prevState) => {
        const updatedOptions = [...prevState.dialogOptionList];
        updatedOptions[index] = value;
        return { ...prevState, dialogOptionList: updatedOptions, newOption: value };
      });
    }
  };

  const handleDelete = (event, index) => {
    event.stopPropagation();
    if (isEdit === index) {
      setIsEdit(-1);
      setState((prevState) => ({ ...prevState, newOption: "" }));
    } else if (isEdit > index) {
      setIsEdit(isEdit - 1);
    }
    setState((prevState) => {
      const updatedOptions = [...prevState.dialogOptionList];
      updatedOptions.splice(index, 1);
      return { ...prevState, dialogOptionList: updatedOptions };
    });
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const updatedList = Array.from(dialogOptionList);
    const [movedItem] = updatedList.splice(source.index, 1);
    updatedList.splice(destination.index, 0, movedItem);
    let newIsEdit = isEdit;
    if (isEdit === source.index) {
      newIsEdit = destination.index;
    } else if (isEdit > source.index && isEdit <= destination.index) {
      newIsEdit = isEdit - 1;
    } else if (isEdit < source.index && isEdit >= destination.index) {
      newIsEdit = isEdit + 1;
    }
    setState((prevState) => ({ ...prevState, dialogOptionList: updatedList }));
    if (newIsEdit !== isEdit) setIsEdit(newIsEdit);
  };

  const onDragStart = () => {
    if (isEdit !== -1) handleOptionBlur();
  };

  const getGlobalLists = useCallback(async (pageNum = 1, reset = false, searchText, tags) => {
    if (isLoadingRef.current || (!hasMoreRef.current && pageNum > 1 && !reset)) {
      return [];
    }
    isLoadingRef.current = true;
    try {
      const baseUrl = origin();
      let sessionAPIKey = sessionStorage.getItem("CK");
      let sessionSecretKey = sessionStorage.getItem("SK");
      let params = `?rows_per_page=30&page=${pageNum}&search=${searchText || ""}`;
      if (tags) {
        const filterParams = tags?.map((tag, index) => `filter_tags[${index}]=${tag}`).join("&");
        params += `&${filterParams}`;
      }
      if (isTexasTemplate !== undefined && isTexasTemplate !== null) {
        params += `&is_texas=${isTexasTemplate ? 1 : 0}`;
      }
      const response = await axios.get(`${baseUrl}lists${params}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Base64.decode(sessionSecretKey)}`,
          "api-key": `${Base64.decode(sessionAPIKey)}`,
          platform: "web",
          "api-platform": "WRW",
        }
      });
      const data = response?.data?.data?.data || [];
      const totalPages = response?.data?.data?.pagination?.total_pages || 1;
      if (reset || pageNum === 1) {
        setState(prevState => ({ ...prevState, globalLists: data }));
        hasMoreRef.current = totalPages > 1;
        pageRef.current = 1;
      } else {
        setState(prevState => ({ ...prevState, globalLists: Array.isArray(prevState?.globalLists) ? [...prevState?.globalLists, ...data] : [...data] }));
        hasMoreRef.current = pageNum < totalPages;
        pageRef.current = pageNum;
      }
      return data;
    } catch (error) {
      console.error("Error fetching global lists:", error);
      return [];
    } finally {
      isLoadingRef.current = false;
    }
  }, [page, searchData, selectedFilterTagID]);

  useEffect(() => {
    if (open !== "global_lists") return;

    isLoadingRef.current = false;
    hasMoreRef.current = true;
    pageRef.current = 1;
    isInitialMountRef.current = false;

    getGlobalLists(1, true, searchData, selectedFilterTagID);
  }, [open, globalListsFetchNonce, getGlobalLists, searchData, selectedFilterTagID]);

  const hasValidOptions = () => {
    return dialogOptionList?.some((option) => option && option.trim().length > 0);
  };

  const IOSSwitch = styled((props) => (
    <Switch checked={saveToGlobalLists} onChange={(e) => setState(prevState => ({ ...prevState, saveToGlobalLists: e.target.checked, tags: defaultCategories }))} focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: '#239D80',
          opacity: 1,
          border: 0,
          ...theme.applyStyles('dark', { backgroundColor: '#239D80' }),
        },
        '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': { color: '#33cf4d', border: '6px solid #fff' },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.grey[100],
        ...theme.applyStyles('dark', { color: theme.palette.grey[600] }),
      },
      '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.7, ...theme.applyStyles('dark', { opacity: 0.3 }) },
    },
    '& .MuiSwitch-thumb': { boxSizing: 'border-box', width: 22, height: 22, },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: '#E9E9EA',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], { duration: 500 }),
      ...theme.applyStyles('dark', { backgroundColor: '#39393D' }),
    },
  }));

  const renderEditOptionsContent = (component) => {
    return (
      <>
        {open === "global_lists" ?
          <GlobalListOptions
            setState={setState}
            globalLists={globalLists}
            filterTagList={filterTagList}
            getGlobalLists={getGlobalLists}
            isLoading={isLoadingRef.current}
            hasMore={hasMoreRef.current}
            currentPage={pageRef.current}
            selectedFilterTagID={selectedFilterTagID}
            setSelectedFilterTagID={setSelectedFilterTagID}
            searchData={searchData}
            setSearchData={setSearchData}
            page={page} setPage={setPage}
            addDropdown={addDropdown}
            id={id}
            categoryId={categoryId}
            isInitialMountRef={isInitialMountRef}
          /> : <Grid container>
            {rwSettingAccess && !listId ? <Grid item xs={12} mb={1} ml={component === "drawer" ? 2.3 : 0}>
              <FormGroup>
                <Box display="flex" alignItems="center" gap={1}><IOSSwitch disabled={open === "edit_list" && listId !== undefined}
                  sx={{ my: component === "drawer" ? 1 : 0, mx: 1, mb: 1 }} /><Typography className="save_to_global_lists" sx={{ cursor: "default", userSelect: "none" }}>
                    Save to global lists
                  </Typography>
                </Box>
              </FormGroup>
            </Grid> : null}
            {saveToGlobalLists ? <Grid container>
              <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                <Typography className="edit_dialog_subtitle">Name</Typography>
                <TextField
                  value={(!name || /^dropdown[_ ]?[a-f0-9\-]{36}$/i.test(name.trim())) ? '' : name}
                  sx={{ marginLeft: "10px", paddingRight: "20px", }}
                  fullWidth
                  size="small"
                  onChange={(e) => setState(prevState => ({ ...prevState, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                <Typography className="edit_dialog_subtitle">Tags</Typography>
                <Autocomplete
                  className="forms-control-tag custom-color autocompleteCustom"
                  multiple
                  size="small"
                  limitTags={2}
                  options={finalFilterTagList}
                  value={tags}
                  inputValue={tagInputValue}
                  onInputChange={(event, newInputValue, reason) => {
                    setTagInputValue(newInputValue)
                  }}
                  open={tagOpen}
                  componentsProps={{ popper: { sx: { zIndex: 100000 } } }}
                  onOpen={handleTagOpen}
                  onClose={handleTagClose}
                  disableCloseOnSelect
                  clearOnBlur={false}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === (value?.id || value?.pivot?.tag_id)}
                  filterOptions={(options, { inputValue }) => options.filter((opt) => opt?.name.toLowerCase().includes(inputValue.toLowerCase()))}
                  onChange={(e, value) => setState((prev) => ({ ...prev, tags: value }))}
                  sx={{
                    marginLeft: "10px",
                    paddingRight: "20px",
                    "& .MuiOutlinedInput-root": {
                      minHeight: "36px",
                      alignItems: "center",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ccc" },
                    },
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => option?.name && (
                      <Chip
                        style={{
                          borderRadius: "35px",
                          border: "1px solid #CED2DA",
                          backgroundColor: option.color,
                          color: getTextColor(option.color),
                          maxWidth: 120,
                        }}
                        key={option?.id}
                        label={option?.name}
                        variant="outlined"
                        deleteIcon={<CloseIcon sx={{ color: `${getTextColor(option.color)} !important`, fontSize: 16 }} />}
                        {...getTagProps({ index })}
                        sx={{ fontSize: "10px", height: "22px", paddingLeft: "2px !important" }}
                      />
                    ))
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option?.id}>
                      <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: option.color, marginRight: 1 }} />
                      {option?.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        style: { fontFamily: "Open Sans!important", fontSize: "14px", fontWeight: 400, paddingLeft: tags?.length !== 0 ? "0px" : "20px" },
                        endAdornment: (
                          <>
                            <SearchIcon className={`customSearch ${tags?.length !== 0 ? "hidden" : ""}`} style={{ color: "#9b9b9b", marginRight: "8px" }} />
                            <SvgIcon className={`customArrowDown ${tagOpen ? "arrowActive" : ""}`} viewBox="" onClick={!tagOpen ? handleTagOpen : handleTagClose}>
                              {arrowDown}
                            </SvgIcon>
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                <Typography className="edit_dialog_subtitle">Description</Typography>
                <TextField value={description} multiline rows={4} sx={{ marginLeft: "10px", paddingRight: "20px", }} fullWidth size="small" onChange={(e) => setState(prevState => ({ ...prevState, description: e.target.value }))} />
              </Grid>
            </Grid> : null}
            <Typography className="edit_dialog_subtitle" sx={{ px: component === "drawer" ? 2 : 0 }}>Options</Typography>
            <Grid item xs={12} ml={1.3} mb={1.5} px={component === "drawer" ? 2 : 0}>
              <Button
                startIcon={<AddCircleOutlineOutlinedIcon sx={{ fontSize: "18px" }} />}
                className="add-option-button"
                variant="outlined"
                onClick={() => {
                  setState((prevState) => ({
                    ...prevState,
                    dialogOptionList: ["", ...(prevState.dialogOptionList || [])],
                    newOption: ""
                  }));
                  setIsEdit(0);
                }}>
                Add new option
              </Button>
            </Grid>
            {dialogOptionList?.length === 0 && (
              <Grid item xs={12} mb={2} px={component === "drawer" ? 2 : 0}>
                <Typography sx={{ fontSize: "14px", fontWeight: 500, alignItems: "center", justifyContent: "center", textAlign: "center", color: "#777777" }}>
                  At least one option is required to add a list.
                </Typography>
              </Grid>
            )}
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div style={{ width: "100%" }} ref={provided.innerRef} {...provided.droppableProps}>
                    {dialogOptionList?.map((option, index) => (
                      <Draggable key={`option_${index}`} draggableId={String(`option_${index}`)} index={index}>
                        {(provided) => (
                          <Grid container item xs={12} display={"flex"} ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} key={`option-grid-${index}`} px={component === "drawer" ? 2 : 0}>
                            <Grid item xs={component === "drawer" ? 11 : 11.5} className="option-element">
                              <div {...provided.dragHandleProps}>
                                <DragIndicatorIcon className="option-drag-icon" />
                              </div>
                              <TextField
                                value={isEdit === index ? newOption : option}
                                size="small"
                                onClick={() => handleOptionClick(option, index)}
                                onChange={(e) => handleOptionChange(e, index)}
                                onBlur={() => handleOptionBlur(index)}
                                className="option-textfield"
                                inputProps={{ maxLength: 250 }}
                                autoFocus={isEdit === index}
                                inputRef={isEdit === index ? editInputRef : null}
                              />
                            </Grid>
                            <Grid item xs={component === "drawer" ? 1 : 0.5} mt={-0.3} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}>
                              <IconButton onClick={(event) => handleDelete(event, index)}>
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>}</>)
  }

  const renderEditOptionsTitle = (component) => {
    return (<Grid container display={"flex"} alignItems={"center"}>
      {component === "drawer" ? <Grid item xs={component === "drawer" ? 1 : 0.3} display={"flex"} justifyContent={"flex-end"} pr={component === "drawer" ? 2 : 1} ><ArrowBackIcon className="edit_dialog_close" onClick={handleCloseDialog} /></Grid> : null}
      <Grid item display={"flex"} justifyContent={"flex-start"} alignItems={"center"} gap={2} xs={11} pl={component === "drawer" ? 0 : 1}>
        <Typography className="edit-dialog-title">
          {open === "global_lists" ? "Global lists" : open === "new_list" ? "New list" : "Edit list"}
        </Typography>
        {((open === "new_list" || open === "edit_list") && saveToGlobalLists) ? <Chip className={saveToGlobalLists ? "global_chip" : null} label={saveToGlobalLists ? "Global list" : null} /> : null}
      </Grid>
      {component === "drawer" ? null : <Grid item xs={1} display={"flex"} justifyContent={"flex-end"} alignItems={"center"}><CloseIcon className="edit_dialog_close" onClick={handleCloseDialog} /></Grid>}
    </Grid>)
  }

  const renderEditOptionsActions = (component) => {
    return (
      open !== "global_lists" ? <Grid container my={0.6} display={"flex"} alignItems={"center"} justifyContent={component === "drawer" ? "" : "space-between"}>
        {open === "edit_list" ? <Grid item xs={6} ml={0.5} display={"flex"} justifyContent={"flex-start"}>
          <Button
            startIcon={<DeleteIcon sx={{ fontSize: "18px" }} />}
            className="add-delete-button"
            onClick={() => { handleDeleteDropdown(state?.selectedDropdown); handleCloseDialog() }}
            sx={{ textWrap: "nowrap" }}
          >
            Delete List
          </Button>
        </Grid> : null}
        <Grid item xs={open === "edit_list" ? 5.75 : 11.85} mr={component === "drawer" ? 0 : 2} display={"flex"} justifyContent={"flex-end"} gap={1}>
          <Button className="edit-dialog-cancel-btn" onClick={() => handleCloseDialog()}>
            Cancel
          </Button>
          <Button className={!hasValidOptions() === true ? "edit-dialog-save-btn-disabled" : "edit-dialog-save-btn"} disabled={!hasValidOptions()} variant="contained" onClick={(event) => handleSave(event)}>
            {open === "edit_list" ? !listId && saveToGlobalLists ? "Save" : "Update" : "Save"}
          </Button>
        </Grid>
      </Grid > : null)
  };

  return (
    id === "intro-text-editor" ?
      <Dialog id={id} ref={dialogRef} open={Boolean(open)} onClose={handleCloseDialog} classes={{ paper: "edit-dialog-paper" }} maxWidth="xl">
        <DialogTitle>
          {renderEditOptionsTitle("dialog")}
        </DialogTitle>
        <Divider className="edit-dialog-divider" />
        <DialogContent sx={{ padding: open === "global_lists" ? "0px" : "20px 24px" }}>
          {renderEditOptionsContent("dialog")}
        </DialogContent>
        <Divider className="edit-dialog-divider" />
        <DialogActions>
          {renderEditOptionsActions("dialog")}
        </DialogActions>
      </Dialog > : <Drawer open={Boolean(open)} onClose={handleCloseDialog} anchor={"right"} sx={{ zIndex: 99999, overflow: "hidden" }} PaperProps={{ className: "global_list_drawer" }} >
        {renderEditOptionsTitle("drawer")}
        <Divider className="edit-dialog-divider" />
        <Box sx={{ height: "100%", marginTop: "10px", overflow: "auto" }}>
          {renderEditOptionsContent("drawer")}
        </Box>
        <Divider className="edit-dialog-divider" />
        {renderEditOptionsActions("drawer")}
      </Drawer >
  );
}

export default EditOptionsDialog;